import { Link } from '@tanstack/react-router'
import { GENERATION_TYPE_LABELS } from '../../../../../shared-generation-pricing-config'
import { isGenerationModel } from '../types/unified-model.types'
import { parseGenerationId } from '../utils/generationModel'
import { ModelDetailSection } from './ModelDetailSection'
import type { GenerationModelFamily } from '../../../../../shared-generation-pricing-config'
import type { GenerationModelMetadata, GenerationModelPricing, UnifiedModel } from '../types/unified-model.types'
import { GENERATION_FAMILY_LABELS, formatCost, formatPriceUnit } from '@/shared/utils/generationPricing'
import { ModelFamilyIcon } from '@/shared/components/icons/ModelFamilyIcon'
import { useSpotlight } from '@/shared/hooks/useSpotlight'

type GenModel = UnifiedModel & { metadata: GenerationModelMetadata; pricing: GenerationModelPricing }

interface ModelDetailGenerationRelatedProps {
  model: GenModel
  baseModel: string
  allModels: Array<UnifiedModel>
}

const LIMIT = 4
const SIMILAR_LIMIT = 3

export function ModelDetailGenerationRelated({ model, baseModel, allModels }: ModelDetailGenerationRelatedProps) {
  const genModels = allModels.filter((m): m is GenModel => isGenerationModel(m) && m.id !== model.id)

  const otherTypes = genModels.filter((m) => parseGenerationId(m.id).baseModel === baseModel)

  const moreFromFamily = genModels
    .filter((m) => m.family === model.family && parseGenerationId(m.id).baseModel !== baseModel)
    .sort((a, b) => Number(b.featured ?? false) - Number(a.featured ?? false))
    .slice(0, LIMIT)

  const similarModels = genModels
    .filter((m) => m.pricing.generationType === model.pricing.generationType && parseGenerationId(m.id).baseModel !== baseModel)
    .map((m) => {
      const overlap = (m.metadata.supportedParams ?? []).filter((p) => (model.metadata.supportedParams ?? []).includes(p)).length
      const priceDelta = Math.abs(m.pricing.priceMillidollars - model.pricing.priceMillidollars)
      return { model: m, overlap, priceDelta }
    })
    .sort((a, b) => b.overlap - a.overlap || a.priceDelta - b.priceDelta)
    .slice(0, SIMILAR_LIMIT)
    .map((entry) => entry.model)

  if (otherTypes.length === 0 && moreFromFamily.length === 0 && similarModels.length === 0) return null

  return (
    <ModelDetailSection title="Related models">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {otherTypes.length > 0 && (
          <RelatedGroup title="Other generation types for this model" models={otherTypes} />
        )}
        {moreFromFamily.length > 0 && (
          <RelatedGroup title={`More from ${GENERATION_FAMILY_LABELS[model.family] ?? model.family}`} models={moreFromFamily} />
        )}
        {similarModels.length > 0 && (
          <RelatedGroup title="Similar Models" models={similarModels} />
        )}
      </div>
    </ModelDetailSection>
  )
}

function RelatedGroup({ title, models }: { title: string; models: Array<GenModel> }) {
  return (
    <div>
      <h3 className="eyebrow mb-3">{title}</h3>
      <div className="flex flex-col gap-2">
        {models.map((m) => <RelatedModelCard key={m.id} model={m} />)}
      </div>
    </div>
  )
}

function RelatedModelCard({ model }: { model: GenModel }) {
  const onPointerMove = useSpotlight()
  const { genType } = parseGenerationId(model.id)
  return (
    <Link
      to="/models/$type/$"
      params={{ type: model.pricing.generationType, _splat: model.id }}
      onPointerMove={onPointerMove}
      className="surface-bevel spotlight flex items-center gap-3 rounded-[1.25rem] px-4 py-3.5 transition-colors duration-300 ease-brisk hover:border-[var(--color-border-strong)]"
    >
      <ModelFamilyIcon modelFamily={model.family as GenerationModelFamily} size={24} />
      <div className="min-w-0">
        <p className="text-sm font-semibold text-[var(--color-text-primary)] truncate">
          {model.name} · {GENERATION_TYPE_LABELS[genType] ?? genType}
        </p>
        <p className="font-mono text-xs tabular-nums text-[var(--color-text-tertiary)]">
          {formatCost(model.pricing.priceMillidollars)} {formatPriceUnit(model.pricing.priceUnit)}
        </p>
      </div>
    </Link>
  )
}
